// Kantenlänge, auf die Profilbilder vor dem Upload heruntergerechnet werden.
// Deutlich größer als die Anzeigegröße (max. 100px), damit HiDPI-Displays scharf bleiben.
export const PROFILE_IMAGE_SIZE = 256;

export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
];

/**
 * Rechnet das Bild proportional herunter, sodass es vollständig in ein Quadrat der
 * Kantenlänge `size` passt, und füllt den Rand transparent auf. Es wird nichts
 * abgeschnitten und nichts verzerrt. Ergebnis ist immer ein quadratisches PNG.
 *
 * Bilder, die bereits kleiner als `size` sind, werden nicht hochskaliert.
 */
export async function resizeImageToSquare(
  file: File,
  size: number,
): Promise<File> {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error(
      `Nicht unterstütztes Dateiformat: ${file.type || 'unbekannt'}.`,
    );
  }

  const source = await decodeImage(file);

  try {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas-Kontext nicht verfügbar.');
    }

    // Das min(..., 1) verhindert, dass ein kleines Bild künstlich hochskaliert
    // und dadurch unscharf wird.
    const scale = Math.min(size / source.width, size / source.height, 1);
    const targetWidth = Math.round(source.width * scale);
    const targetHeight = Math.round(source.height * scale);

    // Zentriert zeichnen; der verbleibende Rand bleibt transparent
    ctx.drawImage(
      source.image,
      Math.round((size - targetWidth) / 2),
      Math.round((size - targetHeight) / 2),
      targetWidth,
      targetHeight,
    );

    const blob = await canvasToBlob(canvas);
    return new File([blob], toPngFilename(file.name), { type: 'image/png' });
  } finally {
    source.release();
  }
}

interface DecodedImage {
  image: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

// Dekodiert die Datei und prüft damit zugleich, ob es sich wirklich um ein Bild handelt -
// eine umbenannte Fremddatei scheitert hier, was ein MIME-String allein nicht leisten kann.
async function decodeImage(file: File): Promise<DecodedImage> {
  // imageOrientation: 'from-image' dreht Handy-Fotos gemäß EXIF korrekt aufrecht
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, {
        imageOrientation: 'from-image',
      });
      return {
        image: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close(),
      };
    } catch {
      throw new Error('Die Datei konnte nicht als Bild gelesen werden.');
    }
  }

  return decodeViaImageElement(file);
}

// Fallback für Browser ohne createImageBitmap
function decodeViaImageElement(file: File): Promise<DecodedImage> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      if (!img.naturalWidth || !img.naturalHeight) {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Bild hat keine gültigen Abmessungen.'));
        return;
      }
      resolve({
        image: img,
        width: img.naturalWidth,
        height: img.naturalHeight,
        release: () => URL.revokeObjectURL(objectUrl),
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Die Datei konnte nicht als Bild gelesen werden.'));
    };

    img.src = objectUrl;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Das Bild konnte nicht kodiert werden.'));
      }
    }, 'image/png');
  });
}

// Ergebnis ist immer ein PNG, also muss auch die Endung passen
function toPngFilename(originalName: string): string {
  const dot = originalName.lastIndexOf('.');
  const base = dot > 0 ? originalName.substring(0, dot) : originalName;
  return `${base || 'profilbild'}.png`;
}
